import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Alert,
  Box,
  Typography,
  useTheme
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import type { ParkingLot } from '../../map-editor-shared/store'

/**
 * 신규 맵 프로젝트의 CCTV IP 앞자리 기본값. 현장 표준 대역이라 미리 채워 두되,
 * 입력 필드에서 자유롭게 변경할 수 있다(편집 모드에서는 기존 저장값을 따른다).
 */
const DEFAULT_CCTV_IP_PREFIX = '172.19'

interface CreateLotModalProps {
  isOpen: boolean
  onClose: () => void
  editingLot?: ParkingLot | null
  /**
   * `floorName` is provided only on creation (the modal hides the field when
   * editing an existing lot — floor names are managed inside the editor's
   * floor tabs). Combination of (name, floorName) must be unique workspace-wide.
   */
  onSubmit: (data: { name: string; description?: string; floorName?: string; cctvIpPrefix?: string }) => void
  error?: string | null
}

export default function CreateLotModal({
  isOpen,
  onClose,
  editingLot,
  onSubmit,
  error
}: CreateLotModalProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [floorName, setFloorName] = useState('B1')
  const [cctvIpPrefix, setCctvIpPrefix] = useState(DEFAULT_CCTV_IP_PREFIX)
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && editingLot) {
      setName(editingLot.name)
      setDescription(editingLot.description || '')
      // 편집 시에는 기존 저장값을 그대로 보여준다(빈 값이면 빈 채로).
      setCctvIpPrefix(editingLot.cctvIpPrefix || '')
      // Floor name editing on existing lots happens in the floor-tabs UI, not
      // here — keep the field neutral so it isn't mistaken for "rename floor".
      setFloorName('B1')
    } else if (!isOpen) {
      setName('')
      setDescription('')
      // 신규 생성 기본값으로 복원.
      setCctvIpPrefix(DEFAULT_CCTV_IP_PREFIX)
      setFloorName('B1')
      setLocalError(null)
    }
  }, [isOpen, editingLot])

  useEffect(() => {
    if (error) {
      setLocalError(error)
    }
  }, [error])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    // Validate name
    if (!name.trim()) {
      setLocalError('프로젝트 이름을 입력해주세요')
      return
    }
    // Floor name only required at creation time. On edit we ignore it because
    // editingLot's floors are managed elsewhere.
    if (!editingLot && !floorName.trim()) {
      setLocalError('층 이름을 입력해주세요')
      return
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      cctvIpPrefix: cctvIpPrefix.trim() || undefined,
      ...(editingLot ? {} : { floorName: floorName.trim() }),
    })
  }

  const handleClose = () => {
    setName('')
    setDescription('')
    setCctvIpPrefix(DEFAULT_CCTV_IP_PREFIX)
    setFloorName('B1')
    setLocalError(null)
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'divider'
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6" component="div" sx={{
          fontWeight: 600,
          color: 'text.primary'
        }}>
          {editingLot ? '프로젝트 수정' : '새 프로젝트 만들기'}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'action.hover',
              color: 'text.primary'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          {localError && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                bgcolor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                color: 'error.main',
                '& .MuiAlert-icon': {
                  color: 'error.main'
                }
              }}
            >
              {localError}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="프로젝트 이름"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 건물 A 주차장"
              autoFocus
              required
              inputProps={{ maxLength: 100 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.default',
                  '& fieldset': {
                    borderColor: 'divider'
                  },
                  '&:hover fieldset': {
                    borderColor: 'text.secondary'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary'
                },
                '& .MuiInputBase-input': {
                  color: 'text.primary'
                }
              }}
            />
          </Box>

          {!editingLot && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="층 이름"
                value={floorName}
                onChange={(e) => setFloorName(e.target.value)}
                placeholder="예: B1, 1F, 옥상"
                required
                helperText="동일한 프로젝트 이름과 층 이름 조합은 만들 수 없습니다."
                inputProps={{ maxLength: 50 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.default',
                    '& fieldset': {
                      borderColor: 'divider'
                    },
                    '&:hover fieldset': {
                      borderColor: 'text.secondary'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary'
                  },
                  '& .MuiInputBase-input': {
                    color: 'text.primary'
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'text.secondary'
                  }
                }}
              />
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="설명 (선택사항)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="프로젝트에 대한 설명을 입력하세요..."
              multiline
              rows={4}
              inputProps={{ maxLength: 500 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.default',
                  '& fieldset': {
                    borderColor: 'divider'
                  },
                  '&:hover fieldset': {
                    borderColor: 'text.secondary'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary'
                },
                '& .MuiInputBase-input': {
                  color: 'text.primary'
                }
              }}
            />
          </Box>

          <Box>
            <TextField
              fullWidth
              label="CCTV IP 앞자리"
              value={cctvIpPrefix}
              onChange={(e) => setCctvIpPrefix(e.target.value)}
              placeholder="예: 172.19"
              helperText="DXF 변환 시 카메라 IP를 자동 구성합니다. 도면 라벨 33.14 → 172.19.33.14"
              inputProps={{ maxLength: 50, inputMode: 'text' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.default',
                  '& fieldset': {
                    borderColor: 'divider'
                  },
                  '&:hover fieldset': {
                    borderColor: 'text.secondary'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary'
                },
                '& .MuiInputBase-input': {
                  color: 'text.primary'
                },
                '& .MuiFormHelperText-root': {
                  color: 'text.secondary'
                }
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          pb: 3,
          pt: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'text.secondary',
                bgcolor: 'action.hover',
                color: 'text.primary'
              }
            }}
          >
            취소
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark'
              },
              boxShadow: isDark
                ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                : '0 4px 12px rgba(59, 130, 246, 0.2)'
            }}
          >
            {editingLot ? '수정하기' : '프로젝트 만들기'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
